import { createSlice } from '@reduxjs/toolkit';
import { createAppThunk } from '../createAppThunk';
import {
  addItemToArray,
  isMaternityAssessment,
  isMaternityNotification,
  removeItemFromArray,
  updateItemInArray,
} from './types';
import type {
  AssessmentStatus,
  CreateMaternityNotificationDto,
  CreateMaternityRiskAssessmentDto,
  EnrichedActivityLog,
  GetAssessmentStatisticsData,
  GetEmployeeMaternityStatusData,
  GetNotificationCountsData,
  MaternityNotificationResponseDto,
  MaternityRiskAssessmentResponseDto,
  QuestionnaireResponseDto,
  QuestionnaireResponseResponseDto,
  SubmitQuestionnaireResponseDto,
  UpdateMaternityNotificationDto,
  UpdateQuestionnaireResponseDto,
} from '@/types/Api';
import { EntityTypeEnum } from '@/types/Api';
import {
  createMaternityAssessment,
  createMaternityNotification,
  deleteMaternityNotification,
  fetchActivityLogs,
  getEmployeeMaternityStatus,
  getLatestNotificationByEmployee,
  getMaternityAssessment,
  getMaternityAssessmentsByEmployee,
  getMaternityAssessmentStatistics,
  getMaternityAssessmentTimeline,
  getMaternityNotification,
  getMaternityQuestionnaire,
  getMaternityQuestionnaireResponse,
  getNotificationCounts,
  getNotificationsByEmployee,
  listMaternityAssessments,
  listMaternityNotifications,
  submitMaternityQuestionnaire,
  updateAssessmentStatus,
  updateMaternityNotification,
  updateMaternityQuestionnaire,
} from '@/lib/apiClient';
import type { NormalizedError } from '@/types/NormalizedError';
import { OPTIMISTIC_ACTIVITY_LOG_TTL_MS } from '@/redux/optimisticActivityLog';

interface MaternityRiskAssessmentState {
  notifications: MaternityNotificationResponseDto[];
  assessments: MaternityRiskAssessmentResponseDto[];
  currentNotification: MaternityNotificationResponseDto | null;
  currentAssessment: MaternityRiskAssessmentResponseDto | null;
  currentQuestionnaire: QuestionnaireResponseResponseDto | null;
  employeeMaternityStatus: GetEmployeeMaternityStatusData | null;
  questionnaire: QuestionnaireResponseDto | null;
  notificationCounts: GetNotificationCountsData | null;
  assessmentStatistics: GetAssessmentStatisticsData | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  notificationStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  assessmentStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  questionnaireStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: NormalizedError | null;
  notificationError: NormalizedError | null;
  assessmentError: NormalizedError | null;
  questionnaireError: NormalizedError | null;
  activityLogs: EnrichedActivityLog[];
  pendingOptimisticActivityLogs: EnrichedActivityLog[];
  activityLogsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  activityLogsError: NormalizedError | null;
}

const initialState: MaternityRiskAssessmentState = {
  notifications: [],
  assessments: [],
  currentNotification: null,
  currentAssessment: null,
  currentQuestionnaire: null,
  employeeMaternityStatus: null,
  questionnaire: null,
  notificationCounts: null,
  assessmentStatistics: null,
  status: 'idle',
  notificationStatus: 'idle',
  assessmentStatus: 'idle',
  questionnaireStatus: 'idle',
  error: null,
  notificationError: null,
  assessmentError: null,
  questionnaireError: null,
  activityLogs: [],
  pendingOptimisticActivityLogs: [],
  activityLogsStatus: 'idle',
  activityLogsError: null,
};

const mergeActivityLogs = (
  serverLogs: EnrichedActivityLog[],
  optimisticLogs: EnrichedActivityLog[],
): EnrichedActivityLog[] => {
  const now = Date.now();
  const activeOptimisticLogs = optimisticLogs.filter((entry) => {
    const timestamp = new Date(entry.timestamp).getTime();
    return Number.isFinite(timestamp) && now - timestamp <= OPTIMISTIC_ACTIVITY_LOG_TTL_MS;
  });
  const optimisticIds = new Set(activeOptimisticLogs.map((entry) => entry.id));
  return [...activeOptimisticLogs, ...serverLogs.filter((log) => !optimisticIds.has(log.id))].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );
};

const pruneOptimisticActivityLogs = (
  optimisticLogs: EnrichedActivityLog[],
): EnrichedActivityLog[] => {
  const now = Date.now();
  return optimisticLogs.filter((entry) => {
    const timestamp = new Date(entry.timestamp).getTime();
    return Number.isFinite(timestamp) && now - timestamp <= OPTIMISTIC_ACTIVITY_LOG_TTL_MS;
  });
};

// Async thunks for notifications
export const listNotifications = createAppThunk<
  MaternityNotificationResponseDto[] | undefined,
  { orgId: string; token: string; params?: { startDate?: string; endDate?: string } }
>('maternityRiskAssessment/listNotifications', async ({ orgId, token, params }, thunkAPI) => {
  const response = await listMaternityNotifications(orgId, token, params);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const fetchNotificationsByEmployee = createAppThunk<
  MaternityNotificationResponseDto[] | undefined,
  { orgId: string; employeeId: string; token: string }
>(
  'maternityRiskAssessment/fetchNotificationsByEmployee',
  async ({ orgId, employeeId, token }, thunkAPI) => {
    const response = await getNotificationsByEmployee(orgId, employeeId, token);
    if (response.error) return thunkAPI.rejectWithValue(response.error);
    return response.data;
  },
);

export const fetchLatestNotificationByEmployee = createAppThunk<
  MaternityNotificationResponseDto | undefined,
  { orgId: string; employeeId: string; token: string }
>(
  'maternityRiskAssessment/fetchLatestNotificationByEmployee',
  async ({ orgId, employeeId, token }, thunkAPI) => {
    const response = await getLatestNotificationByEmployee(orgId, employeeId, token);
    if (response.error) return thunkAPI.rejectWithValue(response.error);
    return response.data;
  },
);

export const fetchMaternityActivityLogs = createAppThunk<
  EnrichedActivityLog[] | undefined,
  { orgId: string; token: string }
>('maternityRiskAssessment/fetchActivityLogs', async ({ orgId, token }, thunkAPI) => {
  const response = await fetchActivityLogs(
    orgId,
    token,
    false,
    undefined, // single entityType
    [EntityTypeEnum.MaternityNotification, EntityTypeEnum.MaternityRiskAssessment], // multiple entityTypes
  );
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const fetchNotificationCounts = createAppThunk<
  GetNotificationCountsData | undefined,
  { orgId: string; token: string }
>('maternityRiskAssessment/fetchNotificationCounts', async ({ orgId, token }, thunkAPI) => {
  const response = await getNotificationCounts(orgId, token);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const createNotification = createAppThunk<
  MaternityNotificationResponseDto | undefined,
  { orgId: string; data: CreateMaternityNotificationDto; token: string }
>('maternityRiskAssessment/createNotification', async ({ orgId, data, token }, thunkAPI) => {
  const response = await createMaternityNotification(orgId, data, token);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const updateNotification = createAppThunk<
  MaternityNotificationResponseDto | undefined,
  { orgId: string; notificationId: string; data: UpdateMaternityNotificationDto; token: string }
>(
  'maternityRiskAssessment/updateNotification',
  async ({ orgId, notificationId, data, token }, thunkAPI) => {
    const response = await updateMaternityNotification(orgId, notificationId, data, token);
    if (response.error) return thunkAPI.rejectWithValue(response.error);
    return response.data;
  },
);

export const deleteNotification = createAppThunk<
  { notificationId: string },
  { orgId: string; notificationId: string; token: string }
>(
  'maternityRiskAssessment/deleteNotification',
  async ({ orgId, notificationId, token }, thunkAPI) => {
    const response = await deleteMaternityNotification(orgId, notificationId, token);
    if (response.error) return thunkAPI.rejectWithValue(response.error);
    return { notificationId };
  },
);

export const getNotification = createAppThunk<
  MaternityNotificationResponseDto | undefined,
  { orgId: string; notificationId: string; token: string }
>('maternityRiskAssessment/getNotification', async ({ orgId, notificationId, token }, thunkAPI) => {
  const response = await getMaternityNotification(orgId, notificationId, token);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

// Async thunks for assessments
export const listAssessments = createAppThunk<
  MaternityRiskAssessmentResponseDto[] | undefined,
  { orgId: string; token: string; params?: { completed?: boolean; status?: AssessmentStatus } }
>('maternityRiskAssessment/listAssessments', async ({ orgId, token, params }, thunkAPI) => {
  const response = await listMaternityAssessments(orgId, token, params);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const getAssessment = createAppThunk<
  MaternityRiskAssessmentResponseDto | undefined,
  { orgId: string; assessmentId: string; token: string }
>('maternityRiskAssessment/getAssessment', async ({ orgId, assessmentId, token }, thunkAPI) => {
  const response = await getMaternityAssessment(orgId, assessmentId, token);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const getAssessmentsByEmployee = createAppThunk<
  MaternityRiskAssessmentResponseDto[] | undefined,
  { orgId: string; employeeId: string; token: string }
>(
  'maternityRiskAssessment/getAssessmentsByEmployee',
  async ({ orgId, employeeId, token }, thunkAPI) => {
    const response = await getMaternityAssessmentsByEmployee(orgId, employeeId, token);
    if (response.error) return thunkAPI.rejectWithValue(response.error);
    return response.data;
  },
);

export const fetchAssessmentStatistics = createAppThunk<
  GetAssessmentStatisticsData | undefined,
  { orgId: string; token: string }
>('maternityRiskAssessment/fetchAssessmentStatistics', async ({ orgId, token }, thunkAPI) => {
  const response = await getMaternityAssessmentStatistics(orgId, token);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const fetchAssessmentTimeline = createAppThunk<
  MaternityRiskAssessmentResponseDto[] | undefined,
  { orgId: string; startDate: string; endDate: string; token: string }
>(
  'maternityRiskAssessment/fetchAssessmentTimeline',
  async ({ orgId, startDate, endDate, token }, thunkAPI) => {
    const response = await getMaternityAssessmentTimeline(orgId, startDate, endDate, token);
    if (response.error) return thunkAPI.rejectWithValue(response.error);
    return response.data;
  },
);

export const createAssessment = createAppThunk<
  MaternityRiskAssessmentResponseDto | undefined,
  { orgId: string; data: CreateMaternityRiskAssessmentDto; token: string }
>('maternityRiskAssessment/createAssessment', async ({ orgId, data, token }, thunkAPI) => {
  const response = await createMaternityAssessment(orgId, data, token);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const updateStatus = createAppThunk<
  MaternityRiskAssessmentResponseDto | undefined,
  { orgId: string; assessmentId: string; status: string; token: string }
>(
  'maternityRiskAssessment/updateStatus',
  async ({ orgId, assessmentId, status, token }, thunkAPI) => {
    const response = await updateAssessmentStatus(orgId, assessmentId, status, token);
    if (response.error) return thunkAPI.rejectWithValue(response.error);
    return response.data;
  },
);

// Async thunks for employee status and questionnaire
export const fetchQuestionnaire = createAppThunk<
  QuestionnaireResponseDto | undefined,
  { orgId: string; token: string }
>('maternityRiskAssessment/fetchQuestionnaire', async ({ orgId, token }, thunkAPI) => {
  const response = await getMaternityQuestionnaire(orgId, token);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const submitMaternityQuestionnaireResponse = createAppThunk<
  QuestionnaireResponseResponseDto | undefined,
  { orgId: string; assessmentId: string; data: SubmitQuestionnaireResponseDto; token: string }
>(
  'maternityRiskAssessment/submitMaternityQuestionnaireResponse',
  async ({ orgId, assessmentId, data, token }, thunkAPI) => {
    const response = await submitMaternityQuestionnaire(orgId, assessmentId, data, token);
    if (response.error) return thunkAPI.rejectWithValue(response.error);
    return response.data;
  },
);

export const updateMaternityQuestionnaireResponse = createAppThunk<
  QuestionnaireResponseResponseDto | undefined,
  { orgId: string; assessmentId: string; data: UpdateQuestionnaireResponseDto; token: string }
>(
  'maternityRiskAssessment/updateMaternityQuestionnaireResponse',
  async ({ orgId, assessmentId, data, token }, thunkAPI) => {
    const response = await updateMaternityQuestionnaire(orgId, assessmentId, data, token);
    if (response.error) return thunkAPI.rejectWithValue(response.error);
    return response.data;
  },
);

export const getEmployeeStatus = createAppThunk<
  GetEmployeeMaternityStatusData | undefined,
  { orgId: string; employeeId: string; token: string }
>('maternityRiskAssessment/getEmployeeStatus', async ({ orgId, employeeId, token }, thunkAPI) => {
  const response = await getEmployeeMaternityStatus(orgId, employeeId, token);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const getQuestionnaireResponse = createAppThunk<
  QuestionnaireResponseResponseDto | undefined,
  { orgId: string; assessmentId: string; token: string }
>(
  'maternityRiskAssessment/getQuestionnaireResponse',
  async ({ orgId, assessmentId, token }, thunkAPI) => {
    const response = await getMaternityQuestionnaireResponse(orgId, assessmentId, token);
    if (response.error) return thunkAPI.rejectWithValue(response.error);
    return response.data;
  },
);

const maternityRiskAssessmentSlice = createSlice({
  name: 'maternityRiskAssessment',
  initialState,
  reducers: {
    clearCurrentNotification: (state) => {
      state.currentNotification = null;
    },
    clearCurrentAssessment: (state) => {
      state.currentAssessment = null;
    },
    clearCurrentQuestionnaire: (state) => {
      state.currentQuestionnaire = null;
    },
    clearEmployeeStatus: (state) => {
      state.employeeMaternityStatus = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.notificationError = null;
      state.assessmentError = null;
      state.questionnaireError = null;
    },
    clearAssessments: (state) => {
      state.assessments = [];
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    clearStatistics: (state) => {
      state.notificationCounts = null;
      state.assessmentStatistics = null;
    },
    addOptimisticMaternityActivityLog: (state, action) => {
      state.pendingOptimisticActivityLogs = pruneOptimisticActivityLogs(
        state.pendingOptimisticActivityLogs,
      );
      state.pendingOptimisticActivityLogs.unshift(action.payload);
      state.activityLogs = mergeActivityLogs(
        state.activityLogs,
        state.pendingOptimisticActivityLogs,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // List notifications
      .addCase(listNotifications.pending, (state) => {
        state.notificationStatus = 'loading';
        state.notificationError = null;
      })
      .addCase(listNotifications.fulfilled, (state, action) => {
        state.notificationStatus = 'succeeded';

        if (action.payload) {
          state.notifications = action.payload;
        }
      })
      .addCase(listNotifications.rejected, (state, action) => {
        state.notificationStatus = 'failed';
        state.notificationError = action.payload ?? null;
      })
      // Create notification
      .addCase(createNotification.pending, (state) => {
        state.notificationStatus = 'loading';
        state.notificationError = null;
      })
      .addCase(createNotification.fulfilled, (state, action) => {
        state.notificationStatus = 'succeeded';
        if (isMaternityNotification(action.payload)) {
          state.currentNotification = action.payload;
          state.notifications = addItemToArray(state.notifications, action.payload, 'start');
        } else {
          console.error('Invalid notification received:', action.payload);
        }
      })
      .addCase(createNotification.rejected, (state, action) => {
        state.notificationStatus = 'failed';
        state.notificationError = action.payload ?? null;
      })
      // Update notification
      .addCase(updateNotification.pending, (state) => {
        state.notificationStatus = 'loading';
        state.notificationError = null;
      })
      .addCase(updateNotification.fulfilled, (state, action) => {
        state.notificationStatus = 'succeeded';
        if (isMaternityNotification(action.payload)) {
          state.currentNotification = action.payload;
          state.notifications = updateItemInArray(
            state.notifications,
            action.payload.id,
            action.payload,
          );
        } else {
          console.error('Invalid notification received:', action.payload);
        }
      })
      .addCase(updateNotification.rejected, (state, action) => {
        state.notificationStatus = 'failed';
        state.notificationError = action.payload ?? null;
      })
      // Delete notification
      .addCase(deleteNotification.pending, (state) => {
        state.notificationStatus = 'loading';
        state.notificationError = null;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.notificationStatus = 'succeeded';
        const { notificationId } = action.payload;
        state.notifications = removeItemFromArray(state.notifications, notificationId);
        if (state.currentNotification?.id === notificationId) {
          state.currentNotification = null;
        }
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.notificationStatus = 'failed';
        state.notificationError = action.payload ?? null;
      })
      // Get notification
      .addCase(getNotification.pending, (state) => {
        state.notificationStatus = 'loading';
        state.notificationError = null;
      })
      .addCase(getNotification.fulfilled, (state, action) => {
        state.notificationStatus = 'succeeded';
        state.currentNotification = action.payload as MaternityNotificationResponseDto;
      })
      .addCase(getNotification.rejected, (state, action) => {
        state.notificationStatus = 'failed';
        state.notificationError = action.payload ?? null;
      })
      // Get assessment
      .addCase(getAssessment.pending, (state) => {
        state.assessmentStatus = 'loading';
        state.assessmentError = null;
      })
      .addCase(getAssessment.fulfilled, (state, action) => {
        state.assessmentStatus = 'succeeded';
        state.currentAssessment = action.payload as MaternityRiskAssessmentResponseDto;
        // Always clear questionnaire when loading new assessment to prevent stale data
        state.currentQuestionnaire = null;
      })
      .addCase(getAssessment.rejected, (state, action) => {
        state.assessmentStatus = 'failed';
        state.assessmentError = action.payload ?? null;
      })
      // Create assessment
      .addCase(createAssessment.pending, (state) => {
        state.assessmentStatus = 'loading';
        state.assessmentError = null;
      })
      .addCase(createAssessment.fulfilled, (state, action) => {
        state.assessmentStatus = 'succeeded';
        if (isMaternityAssessment(action.payload)) {
          state.currentAssessment = action.payload;
          // Clear questionnaire for new assessment
          state.currentQuestionnaire = null;
          // Add to assessments array to maintain consistency
          state.assessments = addItemToArray(state.assessments, action.payload, 'start');
        } else {
          console.error('Invalid assessment received:', action.payload);
        }
      })
      .addCase(createAssessment.rejected, (state, action) => {
        state.assessmentStatus = 'failed';
        state.assessmentError = action.payload ?? null;
      })
      // Submit questionnaire response
      .addCase(submitMaternityQuestionnaireResponse.pending, (state) => {
        state.questionnaireStatus = 'loading';
        state.questionnaireError = null;
      })
      .addCase(submitMaternityQuestionnaireResponse.fulfilled, (state, action) => {
        state.questionnaireStatus = 'succeeded';
        state.currentQuestionnaire = action.payload ?? null;
        // Clear current assessment to force refresh with updated status
        state.currentAssessment = null;
      })
      .addCase(submitMaternityQuestionnaireResponse.rejected, (state, action) => {
        state.questionnaireStatus = 'failed';
        state.questionnaireError = action.payload ?? null;
      })
      // Update questionnaire response
      .addCase(updateMaternityQuestionnaireResponse.pending, (state) => {
        state.questionnaireStatus = 'loading';
        state.questionnaireError = null;
      })
      .addCase(updateMaternityQuestionnaireResponse.fulfilled, (state, action) => {
        state.questionnaireStatus = 'succeeded';
        state.currentQuestionnaire = action.payload ?? null;
        // Clear current assessment to force refresh with updated status
        state.currentAssessment = null;
      })
      .addCase(updateMaternityQuestionnaireResponse.rejected, (state, action) => {
        state.questionnaireStatus = 'failed';
        state.questionnaireError = action.payload ?? null;
      })
      // Update status
      .addCase(updateStatus.pending, (state) => {
        state.assessmentStatus = 'loading';
        state.assessmentError = null;
      })
      .addCase(updateStatus.fulfilled, (state, action) => {
        state.assessmentStatus = 'succeeded';
        if (isMaternityAssessment(action.payload)) {
          state.currentAssessment = action.payload;
          // Clear questionnaire when status updates as assessment has changed
          state.currentQuestionnaire = null;
          // Update in assessments array to maintain consistency
          state.assessments = updateItemInArray(
            state.assessments,
            action.payload.id,
            action.payload,
          );
        } else {
          console.error('Invalid assessment received:', action.payload);
        }
      })
      .addCase(updateStatus.rejected, (state, action) => {
        state.assessmentStatus = 'failed';
        state.assessmentError = action.payload ?? null;
      })
      // Get employee status
      .addCase(getEmployeeStatus.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getEmployeeStatus.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.employeeMaternityStatus = action.payload as GetEmployeeMaternityStatusData;
      })
      .addCase(getEmployeeStatus.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? null;
      })
      // Fetch questionnaire
      .addCase(fetchQuestionnaire.pending, (state) => {
        state.questionnaireStatus = 'loading';
        state.questionnaireError = null;
      })
      .addCase(fetchQuestionnaire.fulfilled, (state, action) => {
        state.questionnaireStatus = 'succeeded';
        state.questionnaire = action.payload ?? null;
      })
      .addCase(fetchQuestionnaire.rejected, (state, action) => {
        state.questionnaireStatus = 'failed';
        state.questionnaireError = action.payload ?? null;
      })
      // List assessments
      .addCase(listAssessments.pending, (state) => {
        state.assessmentStatus = 'loading';
        state.assessmentError = null;
      })
      .addCase(listAssessments.fulfilled, (state, action) => {
        state.assessmentStatus = 'succeeded';
        state.assessments = action.payload as MaternityRiskAssessmentResponseDto[];
      })
      .addCase(listAssessments.rejected, (state, action) => {
        state.assessmentStatus = 'failed';
        state.assessmentError = action.payload ?? null;
      })
      // Get assessments by employee
      .addCase(getAssessmentsByEmployee.pending, (state) => {
        state.assessmentStatus = 'loading';
        state.assessmentError = null;
      })
      .addCase(getAssessmentsByEmployee.fulfilled, (state, action) => {
        state.assessmentStatus = 'succeeded';
        state.assessments = action.payload as MaternityRiskAssessmentResponseDto[];
      })
      .addCase(getAssessmentsByEmployee.rejected, (state, action) => {
        state.assessmentStatus = 'failed';
        state.assessmentError = action.payload ?? null;
      })
      // Get notifications by employee
      .addCase(fetchNotificationsByEmployee.pending, (state) => {
        state.notificationStatus = 'loading';
        state.notificationError = null;
      })
      .addCase(fetchNotificationsByEmployee.fulfilled, (state, action) => {
        state.notificationStatus = 'succeeded';
        state.notifications = action.payload as MaternityNotificationResponseDto[];
      })
      .addCase(fetchNotificationsByEmployee.rejected, (state, action) => {
        state.notificationStatus = 'failed';
        state.notificationError = action.payload ?? null;
      })
      // Get latest notification by employee
      .addCase(fetchLatestNotificationByEmployee.pending, (state) => {
        state.notificationStatus = 'loading';
        state.notificationError = null;
      })
      .addCase(fetchLatestNotificationByEmployee.fulfilled, (state, action) => {
        state.notificationStatus = 'succeeded';
        state.currentNotification = action.payload as MaternityNotificationResponseDto;
      })
      .addCase(fetchLatestNotificationByEmployee.rejected, (state, action) => {
        state.notificationStatus = 'failed';
        state.notificationError = action.payload ?? null;
      })
      // Fetch notification counts
      .addCase(fetchNotificationCounts.pending, (state) => {
        state.notificationStatus = 'loading';
        state.notificationError = null;
      })
      .addCase(fetchNotificationCounts.fulfilled, (state, action) => {
        state.notificationStatus = 'succeeded';
        state.notificationCounts = action.payload as GetNotificationCountsData;
      })
      .addCase(fetchNotificationCounts.rejected, (state, action) => {
        state.notificationStatus = 'failed';
        state.notificationError = action.payload ?? null;
      })
      .addCase(fetchMaternityActivityLogs.pending, (state) => {
        state.activityLogsStatus = 'loading';
        state.activityLogsError = null;
      })
      .addCase(fetchMaternityActivityLogs.fulfilled, (state, action) => {
        state.activityLogsStatus = 'succeeded';
        state.pendingOptimisticActivityLogs = pruneOptimisticActivityLogs(
          state.pendingOptimisticActivityLogs,
        );
        state.activityLogs = mergeActivityLogs(
          (action.payload as EnrichedActivityLog[]) ?? [],
          state.pendingOptimisticActivityLogs,
        );
      })
      .addCase(fetchMaternityActivityLogs.rejected, (state, action) => {
        state.activityLogsStatus = 'failed';
        state.activityLogsError = action.payload ?? null;
      })
      // Fetch assessment statistics
      .addCase(fetchAssessmentStatistics.pending, (state) => {
        state.assessmentStatus = 'loading';
        state.assessmentError = null;
      })
      .addCase(fetchAssessmentStatistics.fulfilled, (state, action) => {
        state.assessmentStatus = 'succeeded';
        state.assessmentStatistics = action.payload as GetAssessmentStatisticsData;
      })
      .addCase(fetchAssessmentStatistics.rejected, (state, action) => {
        state.assessmentStatus = 'failed';
        state.assessmentError = action.payload ?? null;
      })
      // Fetch assessment timeline
      .addCase(fetchAssessmentTimeline.pending, (state) => {
        state.assessmentStatus = 'loading';
        state.assessmentError = null;
      })
      .addCase(fetchAssessmentTimeline.fulfilled, (state, action) => {
        state.assessmentStatus = 'succeeded';
        state.assessments = action.payload as MaternityRiskAssessmentResponseDto[];
      })
      .addCase(fetchAssessmentTimeline.rejected, (state, action) => {
        state.assessmentStatus = 'failed';
        state.assessmentError = action.payload ?? null;
      })
      // Get questionnaire response
      .addCase(getQuestionnaireResponse.pending, (state) => {
        state.questionnaireStatus = 'loading';
        state.questionnaireError = null;
      })
      .addCase(getQuestionnaireResponse.fulfilled, (state, action) => {
        state.questionnaireStatus = 'succeeded';
        state.currentQuestionnaire = action.payload ?? null;
      })
      .addCase(getQuestionnaireResponse.rejected, (state, action) => {
        state.questionnaireStatus = 'failed';
        state.questionnaireError = action.payload ?? null;
        // Clear questionnaire response on failure (e.g., when no response exists for new assessment)
        state.currentQuestionnaire = null;
      });
  },
});

export const {
  clearCurrentNotification,
  clearCurrentAssessment,
  clearCurrentQuestionnaire,
  clearEmployeeStatus,
  clearErrors,
  clearAssessments,
  clearNotifications,
  clearStatistics,
  addOptimisticMaternityActivityLog,
} = maternityRiskAssessmentSlice.actions;

export default maternityRiskAssessmentSlice.reducer;
